/**
 * @author        david.loehr@zumper.com (David Loehr)
 * @copyright     Copyright (c) 2023, Zumper
 */

import { useEffect, useState } from 'react'

/**
 * A polyfilled `ResizeObserver` hook.
 *
 * @param callback
 */
export const useResizeObserver = (
  /**
   * The callback function you'd like to pass to `new ResizeObserver(...)`
   *
   * It will be called when elements change size.
   */
  callback: ResizeObserverCallback
) => {
  const [observer, setObserver] = useState<ResizeObserver | undefined>()

  useEffect(() => {
    let disconnected = false
    let observer: ResizeObserver | undefined
    if (typeof window.ResizeObserver === 'undefined') {
      void import('resize-observer').then(({ install }) => {
        install()
        if (!disconnected) {
          observer = new ResizeObserver(callback)
          setObserver(observer)
        }
      })
    } else {
      observer = new ResizeObserver(callback)
      setObserver(observer)
    }
    return () => {
      disconnected = true
      observer?.disconnect()
      setObserver(undefined)
    }
  }, [callback])

  return observer
}
